<template>
  <div :id="`ticket-note-${note.id}`">
    <loading-screen :is-loading="isSaving"></loading-screen>
    <errors :error-list="serverErrors"></errors>
    <bookmark-modal modelType="TicketNote" :modelId="note.id" :currentValue="note.bookmark"></bookmark-modal>

    <transfer-modal
      :modal-id="`transfer-modal-note-${note.id}`"
      entry-type="ticket_note"
      :entry-id="note.id"
      @transferred="handleTransfer"
    />

    <vue-context ref="menu">
        <li>
            <a href="#" @click.prevent="openCreateExpenseModal($event)">New Expense</a>
        </li>
        <li>
            <a href="#" @click.prevent="openCreateTaskModal($event)">New Task</a>
        </li>
        <!-- <li>
            <a href="#" @click.prevent="ope">Option 2</a>
        </li> -->
    </vue-context>

    <!-- Not Editing -->
    <div v-if="!isEditing" class="card shadow-none" :class="{ 'is-deleted': isDeleted }">
      <div class="card-body" :class="{ 'pb-0': collapsed }">
        <!-- tl-wrapper -->
        <div class="tl-wrapper tl-header">
          <div class="tl-l">
            <div class="avatar-xs">
              <span class="
                  avatar-title
                  rounded
                  bg-soft-primary
                  text-primary
                  font-size-16
                ">
                {{ note.agent.name.charAt(0) }}
              </span>
            </div>
          </div>
          <div class="tl-r">
            <div>
              <span class="name">{{
                isDeleted ? "(Deleted) " : null
              }}{{ note.agent.name }}</span>
              added a private note
            </div>
            <small>
              <i>{{ displayTimestamp(note.created_at) }}</i>
              <span v-if="note.include_in_incident_report && note.incident_report_timestamp" class="ml-2" style="color: #dc3545;">
                <i class="fa fa-clock-o mr-1"></i>
                Incident Report: {{ displayTimestamp(note.incident_report_timestamp) }}
              </span>
              <split-tracking-badge v-if="note.split_tracking" :split-tracking="note.split_tracking" />
            </small>
          </div>
          <div class="tl-actions" v-if="!disableActions">
            <a href="#" class="btn btn-link text-muted" @click.prevent="toggleCollapsed">
              <i class="fa" :class="{
                'fa-chevron-down': collapsed,
                'fa-chevron-up': !collapsed,
              }"></i>
            </a>

              <!-- Incident Report Badge - only show if there's an incident report -->
              <a v-if="incidentReport" href="#" class="btn btn-link text-muted pl-1 pr-1" @click.prevent="toggleIncidentReportInclusion">
              <span class="badge" :class="{
                'badge-success': note.include_in_incident_report,
                'badge-secondary': !note.include_in_incident_report
              }" style="font-size: 10px; padding: 5px 9px;">
                <i v-if="note.include_in_incident_report" class="fa fa-check-circle text-white mr-1"></i>
                <i v-else class="fa fa-times-circle text-white mr-1"></i>
                {{ note.include_in_incident_report ? 'Included in Incident Report' : 'Excluded from Incident Report' }}
              </span>
            </a>

            <a href="#" class="btn btn-link text-muted pl-1 pr-1" @click.prevent="openBookmarkModal">
              <i class="fa fa-heart" :class="{
                'text-danger': note.bookmark
              }"></i>
            </a>

          

            <b-dropdown variant="white" right toggle-class="text-muted pt-0 pr-0 pb-0">
              <template v-slot:button-content>
                <i class="mdi mdi-dots-horizontal font-size-18"></i>
              </template>
              <b-dropdown-item v-if="!isDeleted" @click="edit">Edit
              </b-dropdown-item>
              <b-dropdown-item v-if="!isDeleted" @click="openVersionModal">Versions
              </b-dropdown-item>
              <b-dropdown-item v-if="!isDeleted" @click="openTransferModal">Transfer to other ticket
              </b-dropdown-item>
              <b-dropdown-item v-if="!isDeleted" @click="requestDelete" variant="danger">Delete
              </b-dropdown-item>
              <b-dropdown-item v-if="isDeleted" @click="requestRestore">Restore
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
        <!-- / tl-wrapper -->
        <!-- tl-wrapper -->
        <div class="tl-wrapper" v-show="!collapsed">
          <div class="tl-l"></div>
          <div class="tl-r" @contextmenu.prevent="$refs.menu.open" v-html="note.body"></div>
        </div>
        <!-- / tl-wrapper -->
      </div>
    </div>
    <!-- / Editing -->

    <!-- Editing -->
    <div v-if="isEditing" class="card shadow-none card-action">
      <div class="card-header">
        <div class="avatar-xxs d-inline-block mr-3">
          <span class="
              avatar-title
              rounded
              bg-soft-primary
              text-primary
              font-size-14
            ">
            {{ note.agent.name.charAt(0) }}
          </span>
        </div>
        <b class="d-inline-block">Edit Note</b>
      </div>
      <div class="card-body">
        <editor-content :editor="editor" />
        <!-- Suggestions -->
        <div class="suggestion-list" v-show="showSuggestions" ref="suggestions">
          <template v-if="hasResults">
            <div v-for="(user, index) in filteredUsers" :key="user.id" class="suggestion-list__item"
              :class="{ 'is-selected': navigatedUserIndex === index }" @click="selectUser(user)">
              {{ user.name }}
            </div>
          </template>
          <div v-else class="suggestion-list__item is-empty">
            No users found
          </div>
        </div>
        <!-- / Suggestions -->
      </div>
      
      <!-- Incident Report Timestamp Section - Only show when editing and note is included in incident report -->
      <div v-if="incidentReport && note.include_in_incident_report" class="incident-timestamp-section" style="
        background: #f8f9fa;
        border-top: 1px solid #e9ecef;
        padding: 8px 16px;
        margin: 0 -1px;
      ">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <label for="edit_incident_report_timestamp" class="form-label mb-0 mr-3" style="
              font-size: 12px; 
              color: #6c757d; 
              font-weight: 500;
              margin-bottom: 0;
            ">
              <i class="fa fa-clock-o mr-1" style="color: #6c757d;"></i>
              Override timestamp:
            </label>
            <input
              type="datetime-local"
              id="edit_incident_report_timestamp"
              v-model="incidentReportTimestamp"
              class="form-control form-control-sm"
              style="
                border: 1px solid #ced4da;
                border-radius: 4px;
                font-size: 12px;
                padding: 4px 8px;
                background: white;
                width: 200px;
                transition: border-color 0.2s ease;
              "
              @focus="$event.target.style.borderColor = '#007bff'"
              @blur="$event.target.style.borderColor = '#ced4da'"
            />
          </div>
          <small class="text-muted" style="font-size: 10px;">
            Optional: Override note creation time
          </small>
        </div>
      </div>
      <!-- / Incident Report Timestamp Section -->
      
      <div class="card-menubar">
        <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
          <div class="menubar">
            <a href="#" class="btn btn-itg-white btn-sm menubar__button" :class="{ 'is-active': isActive.bold() }"
              @click="commands.bold">
              <i class="fas fa-bold"></i>
            </a>

            <a href="#" class="btn btn-itg-white btn-sm menubar__button" :class="{ 'is-active': isActive.italic() }"
              @click="commands.italic">
              <i class="fas fa-italic"></i>
            </a>

            <a href="#" class="btn btn-itg-white btn-sm menubar__button" :class="{ 'is-active': isActive.strike() }"
              @click="commands.strike">
              <i class="fas fa-strikethrough"></i>
            </a>

            <a href="#" class="btn btn-itg-white btn-sm menubar__button" :class="{ 'is-active': isActive.underline() }"
              @click="commands.underline">
              <i class="fas fa-underline"></i>
            </a>

            <a href="#" class="btn btn-itg-white btn-sm menubar__button" :class="{ 'is-active': isActive.bullet_list() }"
              @click="commands.bullet_list">
              <i class="fas fa-list-ul"></i>
            </a>

            <a href="#" class="btn btn-itg-white btn-sm menubar__button" :class="{ 'is-active': isActive.ordered_list() }"
              @click="commands.ordered_list">
              <i class="fas fa-list-ol"></i>
            </a>

            <a href="#" class="btn btn-itg-white btn-sm menubar__button" @click="commands.undo">
              <i class="fas fa-undo"></i>
            </a>

            <a href="#" class="btn btn-itg-white btn-sm menubar__button" @click="commands.redo">
              <i class="fas fa-redo"></i>
            </a>

            <a @click.prevent="openMediaUploader" href="#" class="btn btn-itg-white btn-sm menubar__button">
              <i class="fas fa-images"></i>
            </a>
            <upload-media-modal @file-uploaded="(r) => handleFileUploaded(r, commands.image)" :ticket-id="ticket.id"
              modal-id="ticket-note-upload-media-modal"></upload-media-modal>
          </div>
        </editor-menu-bar>
      </div>
      <div class="card-footer text-right">
        <button type="button" class="btn btn-itg mr-2" @click="attemptCancel">
          Cancel
        </button>

        <button type="button" class="btn btn-itg-dark" @click="update">
          Update
        </button>
      </div>
    </div>
    <!-- / Editing -->

    <ticket-note-versions-modal v-if="showVersionModal" :modal-id="versionModalId"
      :note="note"></ticket-note-versions-modal>

    <ticket-note-context-menu-modal :selected-text="selectedText"
      :ticket-note-id="note.id"></ticket-note-context-menu-modal>

      <create-task-right-click-modal :selected-text="selectedText"
      :ticket-note-id="note.id"></create-task-right-click-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Dates from "../../../../../mixins/Dates";
import Forms from "../../../../../mixins/Forms";
import Fuse from "fuse.js";
import tippy, { sticky } from "tippy.js";
import UploadMediaModal from './UploadMediaModal.vue';
import axios from "axios";

import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Italic,
  Link,
  Strike,
  Underline,
  History,
  Mention,
  Image,
} from "tiptap-extensions";
import TicketNoteVersionsModal from "./TicketNote/TicketNoteVersionsModal";
import eventBus from "../../../../../event-bus";
import BookmarkModal from './BookmarkModal.vue';
import TicketNoteContextMenuModal from "./TicketNote/TicketNoteContextMenuModal.vue";
import VueContext from 'vue-context';
import 'vue-context/dist/css/vue-context.css';
import CreateTaskRightClickModal from './CreateTaskRightClickModal.vue';
import SplitTrackingBadge from '../../../../../components/SplitTrackingBadge.vue';
import TransferModal from './TransferModal.vue';

export default {
  props: ["note", "disableActions"],
  name: "TicketNote",

  data() {
    return {
      collapsed: false,

      isSaving: false,
      isEditing: false,
      editor: null,
      serverErrors: [],

      showVersionModal: false,

      selectedText: '',

      query: null,
      suggestionRange: null,
      filteredUsers: [],
      navigatedUserIndex: 0,
      insertMention: () => {
      },
      
      // Incident report timestamp editing
      incidentReportTimestamp: null,
    };
  },

  created() {
    eventBus.$on("collapse-all", () => {
      this.collapsed = true;
    });
    eventBus.$on("expand-all", () => {
      this.collapsed = false;
    });
  },

  updated() {
    eventBus.$on("collapse-all", () => {
      this.collapsed = true;
    });
    eventBus.$on("expand-all", () => {
      this.collapsed = false;
    });
  },

  beforeDestroy() {
    this.destroyPopup();
    eventBus.$off("collapse-all");
    eventBus.$off("expand-all");
  },

  computed: {
    ...mapGetters("ticketViewer", {
      ticket: "ticket",
      agents: "agents",
      incidentReport: "incidentReport",
    }),

    hasResults() {
      return this.filteredUsers.length;
    },
    showSuggestions() {
      return this.query || this.hasResults;
    },
    versionModalId() {
      return "note-version-modal-" + this.note.id;
    },
    isDeleted() {
      return this.note.deleted_at;
    },
  },

  components: { TicketNoteVersionsModal, EditorMenuBar, EditorContent, UploadMediaModal, BookmarkModal, TicketNoteContextMenuModal, VueContext, CreateTaskRightClickModal, SplitTrackingBadge, TransferModal },

  mixins: [Dates, Forms],

  mounted() {
    this.setupEditor();
  },

  methods: {
    ...mapActions("ticketViewer", {
      reloadTicket: "reloadTicket",
      loadMedia: "loadMedia",
    }),

    // handleRightClick(e) {
      // this.openCreateExpenseModal(e);
    // },

    openCreateExpenseModal(e) {
      let vm = this;

      vm.selectedText = '';

      this.$nextTick(() => {
        vm.selectedText = window.getSelection().toString();

        if (vm.selectedText && vm.selectedText.length) {
          e.preventDefault();
          this.$bvModal.show(`ticket-note-context-menu-modal-${vm.note.id}`);
        } else {
          this.$notify.error({title: "Please select some text!", message: "Select some text to create an expense"});
        }
      });
    },

    openCreateTaskModal(e) {
      let vm = this;

      vm.selectedText = '';

      this.$nextTick(() => {
        vm.selectedText = window.getSelection().toString();

        if (vm.selectedText && vm.selectedText.length) {
          e.preventDefault();
          this.$bvModal.show(`create-task-right-click-modal-${vm.note.id}`);
        } else {
          this.$notify.error({title: "Please select some text!", message: "Select some text to create a task"});
        }
      });
    },

    toggleCollapsed() {
      this.collapsed = !this.collapsed;
    },

    requestDelete() {
      let vm = this;
      swal({
        title: "Are you sure you want to delete this note?",
        icon: "warning",
        buttons: {
          cancel: true,
          confirm: "Delete",
        },
        dangerMode: true,
      }).then((shouldDelete) => {
        if (shouldDelete) {
          vm.delete();
        }
      });
    },

    delete() {
      let vm = this;
      vm.isSaving = true;
      axios
        .delete(
          API_BASE + "/tickets/" + this.ticket.id + "/notes/" + this.note.id
        )
        .then((r) => {
          vm.isSaving = false;
          vm.reloadTicket();
          this.$notify.success({
            title: "Success!",
            message: "Note deleted successfully",
          });
        })
        .catch((e) => {
          vm.isSaving = false;
          vm.notifyErrors(e);
        });
    },

    requestRestore() {
      let vm = this;
      swal({
        title: "Are you sure you want to restore this note?",
        icon: "warning",
        buttons: {
          cancel: true,
          confirm: "Restore",
        },
        dangerMode: true,
      }).then((shouldRestore) => {
        if (shouldRestore) {
          vm.restore();
        }
      });
    },

    restore() {
      let vm = this;
      vm.isSaving = true;
      axios
        .post(
          API_BASE +
          "/tickets/" +
          this.ticket.id +
          "/notes/" +
          this.note.id +
          "/restore"
        )
        .then((r) => {
          vm.isSaving = false;
          vm.reloadTicket();
          this.$notify.success({
            title: "Success!",
            message: "Note restored successfully",
          });
        })
        .catch((e) => {
          vm.isSaving = false;
          vm.notifyErrors(e);
        });
    },

    setupEditor() {
      let vm = this;
      this.editor = new Editor({
        extensions: [
          new Blockquote(),
          new CodeBlock(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new BulletList(),
          new OrderedList(),
          new ListItem(),
          new TodoItem(),
          new TodoList(),
          new Bold(),
          new Code(),
          new Italic(),
          new Link(),
          new Strike(),
          new Underline(),
          new History(),
          new Image(),
          new Mention({
            // a list of all suggested items
            items: async () => {
              await new Promise((resolve) => {
                setTimeout(resolve, 500);
              });
              return vm.agents;
            },
            // is called when a suggestion starts
            onEnter: ({ items, query, range, command, virtualNode }) => {
              this.query = query;
              this.filteredUsers = items;
              this.suggestionRange = range;
              this.renderPopup(virtualNode);
              // we save the command for inserting a selected mention
              // this allows us to call it inside of our custom popup
              // via keyboard navigation and on click
              this.insertMention = command;
            },
            // is called when a suggestion has changed
            onChange: ({ items, query, range, virtualNode }) => {
              this.query = query;
              this.filteredUsers = items;
              this.suggestionRange = range;
              this.navigatedUserIndex = 0;
              this.renderPopup(virtualNode);
            },
            // is called when a suggestion is cancelled
            onExit: () => {
              // reset all saved values
              this.query = null;
              this.filteredUsers = [];
              this.suggestionRange = null;
              this.navigatedUserIndex = 0;
              this.destroyPopup();
            },
            // is called on every keyDown event while a suggestion is active
            onKeyDown: ({ event }) => {
              if (event.key === "ArrowUp") {
                this.upHandler();
                return true;
              }
              if (event.key === "ArrowDown") {
                this.downHandler();
                return true;
              }
              if (event.key === "Enter") {
                this.enterHandler();
                return true;
              }
              return false;
            },
            // is called when a suggestion has changed
            // this function is optional because there is basic filtering built-in
            // you can overwrite it if you prefer your own filtering
            // in this example we use fuse.js with support for fuzzy search
            onFilter: async (items, query) => {
              if (!query) {
                return items;
              }
              await new Promise((resolve) => {
                setTimeout(resolve, 500);
              });
              const fuse = new Fuse(items, {
                threshold: 0.2,
                keys: ["name"],
              });
              return fuse.search(query).map((item) => item.item);
            },
          }),
        ],
        content: this.value,
        autoFocus: true,
      });
    },

    edit() {
      this.isEditing = true;
      this.editor.setContent(this.note.body);
      
      // Initialize incident report timestamp for editing
      if (this.note.incident_report_timestamp) {
        this.incidentReportTimestamp = this.convertToLocalDateTime(this.note.incident_report_timestamp);
      } else {
        this.incidentReportTimestamp = null;
      }
    },

    openVersionModal() {
      this.showVersionModal = false;
      this.$nextTick(() => {
        this.showVersionModal = true;
        this.$nextTick(() => {
          this.$bvModal.show(this.versionModalId);
        });
      });
    },

    openBookmarkModal() {
      this.$bvModal.show("bookmark-modal-TicketNote-" + this.note.id);
    },

    openTransferModal() {
      this.$bvModal.show(`transfer-modal-note-${this.note.id}`);
    },

    handleTransfer({ newTicketId }) {
      // Navigate to new ticket after successful transfer
      this.$router.push(`/tickets/${newTicketId}`);
    },

    toggleIncidentReportInclusion() {
      let vm = this;
      vm.isSaving = true;
      vm.serverErrors = [];
      
      const newValue = !this.note.include_in_incident_report;
      
      axios
        .put(
          API_BASE + "/tickets/" + this.ticket.id + "/notes/" + this.note.id,
          {
            include_in_incident_report: newValue,
          }
        )
        .then((r) => {
          vm.isSaving = false;
          vm.note.include_in_incident_report = newValue;
          
          this.$notify.success({
            title: "Success!",
            message: `Note ${newValue ? 'included' : 'excluded'} from incident report`,
          });
        })
        .catch((e) => {
          vm.isSaving = false;
          vm.notifyErrors(e);
        });
    },

    update() {
      let vm = this;
      let editorHtml = vm.editor.getHTML();
      let idsOfUsersMentioned = this.getMentionIdsFromHtml(editorHtml);

      vm.isSaving = true;
      vm.serverErrors = [];
      axios
        .put(
          API_BASE + "/tickets/" + this.ticket.id + "/notes/" + this.note.id,
          {
            body: editorHtml,
            ids_of_users_mentioned: idsOfUsersMentioned,
            include_in_incident_report: this.note.include_in_incident_report,
            incident_report_timestamp: this.note.include_in_incident_report ? this.convertToUtc(this.incidentReportTimestamp) : null,
          }
        )
        .then((r) => {
          vm.isSaving = false;
          vm.isEditing = false;
          vm.note.body = editorHtml;
          
          // Update the incident report timestamp if it was changed
          if (vm.note.include_in_incident_report) {
            vm.note.incident_report_timestamp = vm.convertToUtc(vm.incidentReportTimestamp);
          } else {
            vm.note.incident_report_timestamp = null;
          }

          // this.$notify.success({
          //     title: 'Success!',
          //     message: 'Note updated successfully'
          // });
        })
        .catch((e) => {
          vm.isSaving = false;
          vm.setErrors(e);
        });
    },

    attemptCancel() {
      let vm = this;
      swal({
        title: "Are you sure you want to cancel?",
        text: "You have unsaved changes and your data will be lost.",
        icon: "warning",
        buttons: {
          cancel: true,
          confirm: "Leave without saving",
        },
        dangerMode: true,
      }).then((shouldCancel) => {
        if (shouldCancel) {
          vm.cancel();
        }
      });
    },

    // Timezone conversion methods
    convertToLocalDateTime(utcDateTime) {
      if (!utcDateTime) return null;
      return moment.utc(utcDateTime).tz(window.itgTimezone).format('YYYY-MM-DDTHH:mm');
    },

    convertToUtc(localDateTime) {
      if (!localDateTime) return null;
      return moment.tz(localDateTime, window.itgTimezone).utc().format('YYYY-MM-DD HH:mm:ss');
    },

    cancel() {
      this.isEditing = false;
    },

    // navigate to the previous item
    // if it's the first item, navigate to the last one
    upHandler() {
      this.navigatedUserIndex =
        (this.navigatedUserIndex + this.filteredUsers.length - 1) %
        this.filteredUsers.length;
    },
    // navigate to the next item
    // if it's the last item, navigate to the first one
    downHandler() {
      this.navigatedUserIndex =
        (this.navigatedUserIndex + 1) % this.filteredUsers.length;
    },
    enterHandler() {
      const user = this.filteredUsers[this.navigatedUserIndex];
      if (user) {
        this.selectUser(user);
      }
    },
    // we have to replace our suggestion text with a mention
    // so it's important to pass also the position of your suggestion text
    selectUser(user) {
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: user.id,
          label: user.name,
        },
      });
      this.editor.focus();
    },
    // renders a popup with suggestions
    // tiptap provides a virtualNode object for using popper.js (or tippy.js) for popups
    renderPopup(node) {
      const boundingClientRect = node.getBoundingClientRect();
      const { x, y } = boundingClientRect;
      if (x === 0 && y === 0) {
        return;
      }
      if (this.popup) {
        return;
      }
      // ref: https://atomiks.github.io/tippyjs/v6/all-props/
      this.popup = tippy("#app", {
        getReferenceClientRect: () => boundingClientRect,
        appendTo: () => document.body,
        interactive: true,
        sticky: true, // make sure position of tippy is updated when content changes
        plugins: [sticky],
        content: this.$refs.suggestions,
        trigger: "mouseenter", // manual
        showOnCreate: true,
        theme: "dark",
        placement: "top-start",
        inertia: true,
        duration: [400, 200],
      });
    },
    destroyPopup() {
      if (this.popup) {
        this.popup[0].destroy();
        this.popup = null;
      }
    },
    openMediaUploader() {
      this.$bvModal.show("ticket-note-upload-media-modal");
    },
    handleFileUploaded(r, imgCommand) {
      this.loadMedia();
      let url = r["url"];
      this.$bvModal.hide("ticket-note-upload-media-modal");
      if (url) {
        imgCommand({ src: url });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  /*background-image: linear-gradient(#fef1e1, #fef1e1);*/
  background-image: linear-gradient(#e8f4f8, #e8f4f8);
}

.is-deleted {
  //background: rgba(232, 244, 248, 0.4);
  background: rgba(230, 73, 66, 0.15);
}

// Incident Report Badge Styles
.badge {
  border-radius: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
  
  &.badge-success {
    background-color: #28a745;
    color: white;
    
    &:hover {
      background-color: #218838;
    }
  }
  
  &.badge-secondary {
    background-color: #6c757d;
    color: white;
    
    &:hover {
      background-color: #545b62;
    }
  }
}
</style>
